import { useEffect, useRef, useState } from "react";
import { UploadCloud } from "lucide-react";
import { useFieldResource } from "hooks/useFieldNodes";
import { rtctApi } from "services/rtctApi";
import { fieldTime, fieldValue, formatBytes } from "utils/fieldFormat";

export default function FirmwarePanel({ mac, nodeName }) {
  const [refresh, setRefresh] = useState(0);
  const resource = useFieldResource(mac, rtctApi.ota, refresh);
  const [releaseId, setReleaseId] = useState(""),
    [version, setVersion] = useState(""),
    [file, setFile] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const data = resource.data;
  const releases = data?.available_releases || data?.releases || [];
  const deployment =
    data?.deployments?.[0] || data?.last_deployment || data?.deployment;
  const canManage = data?.can_manage ?? data?.can_manage_ota ?? false;
  const selected = releases.find((r) => String(r.id) === String(releaseId));
  useEffect(() => {
    if (
      !deployment ||
      ["verified", "superseded", "confirmed", "succeeded", "failed", "rolled_back"].includes(
        deployment.status,
      )
    )
      return;
    const timer = setTimeout(() => setRefresh((n) => n + 1), 15000);
    return () => clearTimeout(timer);
  }, [deployment]);
  const upload = async (e) => {
    e.preventDefault();
    if (!file || !version.trim()) return;
    setBusy(true);
    setError("");
    setNotice("");
    const form = new FormData();
    form.append("artifact", file);
    form.append("version", version.trim());
    form.append("target", "main");
    form.append("device_mac", mac);
    try {
      const release = await rtctApi.uploadRelease(form);
      if (!alive.current) return;
      setReleaseId(String(release.id));
      setRefresh((n) => n + 1);
      setNotice(
        `Release ${release.version} uploaded for ${nodeName}. It is not installed until you select Install.`,
      );
    } catch (e) {
      if (alive.current) setError(e.message);
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  const install = async () => {
    if (!selected) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await rtctApi.installRelease(mac, selected.id);
      if (!alive.current) return;
      setNotice(
        `Update ${selected.version} assigned to ${nodeName}. Installation completes after the node checks for updates and confirms a successful boot.`,
      );
      setRefresh((n) => n + 1);
      if (result.status === "failed")
        setError("The update request failed. Refresh to inspect its status.");
    } catch (e) {
      if (alive.current) {
        setError(e.message);
        setRefresh((n) => n + 1);
      }
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  return (
    <section className="field-panel" style={{ marginTop: 22 }}>
      <div className="field-panel-head">
        <div>
          <h2>Firmware updates</h2>
          <p>
            Device-bound release · SHA-256 verification · deliberate
            installation
          </p>
        </div>
        <button
          className="field-button"
          onClick={() => setRefresh((n) => n + 1)}
          disabled={busy}
        >
          Refresh status
        </button>
      </div>
      <div className="field-panel-body">
        {(resource.error || error) && (
          <div className="field-alert" role="alert">
            {error || resource.error}
          </div>
        )}
        <div
          className="field-metrics"
          style={{ gridTemplateColumns: "1fr 1fr", margin: "0 0 18px" }}
        >
          <div className="field-metric">
            <div className="field-metric-label">Reported running firmware</div>
            <strong>
              {fieldValue(
                data?.current_fw || data?.current_firmware || data?.firmware,
              )}
            </strong>
            <small>Confirmed by the node’s report</small>
          </div>
          <div className="field-metric">
            <div className="field-metric-label">Latest deployment</div>
            <strong>
              {deployment?.status?.replaceAll("_", " ") || "No assignment"}
            </strong>
            <small>
              {deployment?.version ? `Release ${deployment.version} · ` : ""}
              {deployment?.updated_at || deployment?.created_at
                ? `${fieldTime(deployment.updated_at || deployment.created_at)} SGT`
                : "Awaiting an approved update"}
            </small>
          </div>
        </div>
        {deployment?.reason && (
          <p className="field-caption">{deployment.reason}</p>
        )}
        {notice && (
          <div className="field-notice" role="status">
            {notice}
          </div>
        )}
        {canManage ? (
          <div className="field-control-grid" style={{ marginTop: 20 }}>
            <form onSubmit={upload}>
              <h3 style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>
                Upload a release for {nodeName}
              </h3>
              <label
                className="field-control-row"
                style={{ display: "block", fontSize: 12 }}
              >
                Version label
                <input
                  aria-label="Firmware version label"
                  type="text"
                  maxLength={64}
                  required
                  value={version}
                  disabled={busy}
                  onChange={(e) => setVersion(e.target.value)}
                  className="field-text-input"
                />
              </label>
              <label
                className="field-control-row"
                style={{ display: "block", fontSize: 12 }}
              >
                Main-board firmware binary
                <input
                  aria-label="Main-board firmware binary"
                  type="file"
                  accept=".bin,application/octet-stream"
                  disabled={busy}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f && (!f.size || f.size > 4 * 1024 * 1024)) {
                      setError(
                        "Select a non-empty firmware binary of at most 4 MiB.",
                      );
                      setFile(null);
                      e.target.value = "";
                    } else {
                      setFile(f || null);
                      setError("");
                    }
                  }}
                  style={{
                    display: "block",
                    marginTop: 8,
                    fontSize: 11,
                    width: "100%",
                  }}
                />
              </label>
              <button
                className="field-button"
                type="submit"
                disabled={busy || !file || !version.trim()}
              >
                <UploadCloud size={15} />
                {busy ? "Working…" : "Upload release"}
              </button>
              <p className="field-caption">
                Upload stores an unapproved binary bound to this node.
                Installation is a separate action.
              </p>
            </form>
            <div>
              <h3 style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>
                Install an available release
              </h3>
              <label htmlFor="ota-release" style={{ fontSize: 12 }}>
                Release for this node
              </label>
              <select
                id="ota-release"
                className="field-text-input"
                value={releaseId}
                disabled={busy}
                onChange={(e) => setReleaseId(e.target.value)}
              >
                <option value="">Select a release</option>
                {releases
                  .filter((r) => r.target === "main")
                  .map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.version} · {formatBytes(r.size || r.byte_size)}
                    </option>
                  ))}
              </select>
              {selected && (
                <div className="field-notice" style={{ margin: "14px 0" }}>
                  <strong>{selected.version}</strong> will be installed on{" "}
                  <strong>{nodeName}</strong> ({mac}).
                  <div
                    style={{
                      fontSize: 10,
                      marginTop: 8,
                      overflowWrap: "anywhere",
                    }}
                  >
                    SHA-256 · {selected.sha256}
                  </div>
                </div>
              )}
              <button
                className="field-button primary"
                disabled={busy || !selected}
                onClick={install}
              >
                {busy ? "Working…" : `Install on ${nodeName}`}
              </button>
              <p className="field-caption">
                Assignment waits for the device’s update check. An assigned
                release is not a confirmed running version. Camera firmware
                installation remains unavailable until validated on this
                hardware.
              </p>
            </div>
          </div>
        ) : (
          <p className="field-caption">
            Your account can view update status. Release upload and installation
            require staff access.
          </p>
        )}
      </div>
    </section>
  );
}
